import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Row } from '@tanstack/react-table';
import type { Invoice } from 'contracts/tracking-v2/invoices';
import { notify } from 'services/notifications';
import { TRACKING_V2_ROUTES } from 'lib/routes';
import { useFetchPaginatedInvoices } from 'hooks/api/invoices';
import { useCreateTrip } from 'hooks/api/trips';
import {
  useInvoicesForTripCreationSearchParams,
} from 'hooks/store/invoices/use-invoices-for-trip-creation-search-params';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import {
  DataTableV2,
  type DataTableV2Ref,
  type DataTableV2SelectionChangeProps,
  type SortingProps,
} from 'components/ui/DataTableV2';
import { Button } from 'components/ui/button';
import {
  TabbedNavigation,
  ClearFiltersButton,
} from 'pages/tracking-v2/shared-components';

import { useInvoiceColumns } from './hooks';
import { getRootCNPJ } from './utils';
import {
  PageTitle,
  InvoiceFilters,
  type InvoiceFiltersRef,
  CreateTripDrawer,
  type CreateTripDrawerRef,
} from './components';

type SelectionRequirements = {
  supplierId: number
  carrierRootCNPJ: string
}

const NAVIGATE_TO_TRIPS_PAGE_AFTER_CRATING_TRIP = true;
const NAVIGATE_TO_TRIP_DETAILS_AFTER_CREATING_TRIP = false;

export const CreateTripPage = () => {
  const navigate = useNavigate();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useInvoicesForTripCreationSearchParams({ persisted: true });
  const { fetchPaginatedInvoices } = useFetchPaginatedInvoices();
  const { createTrip } = useCreateTrip();

  const filtersRef = React.useRef<InvoiceFiltersRef>(null);
  const tableRef = React.useRef<DataTableV2Ref>(null);
  const createTripDrawerRef = React.useRef<CreateTripDrawerRef>(null);

  const [selectionRequirements, setSelectionRequirements]
    = React.useState<SelectionRequirements>();

  const someFiltersAreActive = React.useMemo(() => {
    const { sameCarrierCnpj, ...rest } = filters;
    return Object.values(rest).some(value => !!value);
  }, [filters]);

  const columns = useInvoiceColumns({
    headerCheckboxEnabled: !!selectionRequirements,
    headerCheckboxHint: selectionRequirements
      ? 'Selecionar todas as notas da mesma transportadora e fornecedor presentes nesta página'
      : 'Selecione ao menos uma nota para que possamos identificar sua transportadora e fornecedor',
  });

  const invoicesQuery = useQuery({
    queryKey: [
      'fetch-paginated-invoices-for-trip-creation',
      filters,
      paginationControl,
    ],
    queryFn: () => {
      return fetchPaginatedInvoices({
        filters,
        pagination: paginationControl,
        eligibleForTripCreation: true,
      });
    },
    refetchOnWindowFocus: false,
  });

  const createTripMutation = useMutation({
    mutationKey: ['create-trip'],
    mutationFn: createTrip,
    onSuccess: async (data) => {
      tableRef.current?.clearSelection();
      createTripDrawerRef.current?.close();
      notify('success', 'Viagem criada com sucesso');
      if (NAVIGATE_TO_TRIPS_PAGE_AFTER_CRATING_TRIP) {
        return navigate(TRACKING_V2_ROUTES.tripsTracking().url);
      } else if (NAVIGATE_TO_TRIP_DETAILS_AFTER_CREATING_TRIP) {
        return navigate(TRACKING_V2_ROUTES.tripDetails(data.id).url);
      } else {
        await invoicesQuery.refetch();
      }
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const handleFiltersSubmission = React.useCallback((f: typeof filters) => {
    updateFilters(f);
    updatePaginationControl({ page: 1 });
    tableRef.current?.clearSelection();
  }, [updateFilters, updatePaginationControl]);

  const handleSort = React.useCallback((p: SortingProps) => {
    updatePaginationControl({ ...p, page: 1 });
  }, [updatePaginationControl]);

  const handleFiltersReset = React.useCallback(() => {
    tableRef.current?.clearSelection();
    clearState();
  }, [clearState]);

  const handleDetailsNavigation = React.useCallback((row: Row<Invoice>) => {
    const invoiceId = row.original.id;
    navigate(TRACKING_V2_ROUTES.invoiceDetails(invoiceId).url);
  }, [navigate]);

  const handleShouldEnableRowSelection = React.useCallback((row: Row<Invoice>) => {
    if (!selectionRequirements) {
      return !!row.original.carrier?.document && !!row.original.companyId;
    }

    const rootCnpj = getRootCNPJ(row.original.carrier.document);

    return rootCnpj === selectionRequirements.carrierRootCNPJ &&
      row.original.supplierId === selectionRequirements.supplierId;
  }, [selectionRequirements]);

  const handleDataTableSelection = React.useCallback((selection: DataTableV2SelectionChangeProps<Invoice>) => {
    const { selectedRows } = selection;

    if (!selectedRows.length) {
      return setSelectionRequirements(undefined);
    }

    const carrier = selectedRows[0].carrier;
    const supplierId = selectedRows[0].supplierId;

    if (!carrier.document || !supplierId) {
      return setSelectionRequirements(undefined);
    }

    setSelectionRequirements({
      supplierId,
      carrierRootCNPJ: getRootCNPJ(carrier.document),
    });
  }, []);

  return (
    <Scaffold>
      <CreateTripDrawer
        ref={createTripDrawerRef}
        onSubmit={createTripMutation.mutate}
        loading={createTripMutation.isLoading}
      />
      <TabbedNavigation />
      <div className={'flex flex-col gap-6'}>
        <PageHeaderForFilters
          headerTitle={({ open }) => (
            <>
              <PageTitle />
              <ClearFiltersButton
                visible={!open && someFiltersAreActive}
                onClick={filtersRef.current?.resetFilters}
              />
            </>
          )}
        >
          <InvoiceFilters
            ref={filtersRef}
            onSubmitFilters={handleFiltersSubmission}
            onResetFilters={handleFiltersReset}
          />
        </PageHeaderForFilters>
        <DataTableV2
          ref={tableRef}
          data={invoicesQuery?.data?.data ?? []}
          columns={columns}
          manualPagination
          showControlColumns
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          onRowClick={handleDetailsNavigation}
          isLoading={invoicesQuery.isLoading}
          pagination={{
            ...invoicesQuery?.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
          enableRowSelection={handleShouldEnableRowSelection}
          onSelectionChange={handleDataTableSelection}
          renderTopBar={({ totalRows, selectedRows }) => (
            <div className={'flex items-center gap-4'}>
              <Button
                size={'sm'}
                className={'h-8'}
                disabled={!selectedRows.length}
                onClick={() => createTripDrawerRef.current?.open(selectedRows)}
              >
                Agrupar notas ({selectedRows.length}/{totalRows})
              </Button>
              <Button
                size={'sm'}
                className={'h-8'}
                variant={'outline'}
                disabled={!selectedRows.length}
                onClick={tableRef.current?.clearSelection}
              >
                Limpar seleção
              </Button>
            </div>
          )}
        />
      </div>
    </Scaffold>
  );
};